/**
 * Las herramientas PROPIAS del Catálogo para el agente del Chat
 * (ADR-2026-10-13-catalogo-con-el-agente, ADR-2026-10-14-el-agente-arma-el-catalogo-desde-la-pantalla).
 * Mismo patrón que Flujos y el calendario: viajan al modelo sólo mientras la sesión está montada
 * (`useRegistrarPantalla`) y se resuelven en `ejecutar.ts` con las MISMAS acciones que usan los
 * botones (`usePlanDeSesion`). Nada de esto va a `V2_AGENT_TOOLS`.
 */
import type { HerramientaDePantalla } from '../../../../../chat-atlas';
import { ICONOS_DE_TOMA, MAX_CANTIDAD_TOMA, MAX_TOMAS, TAREAS_DE_IMAGEN } from '../plan';

export const CLAVE_PANTALLA_CATALOGO = 'modo:catalogo';
export const SKILL_DE_CATALOGO = 'catalogo-de-muebles';

const TOMA = {
  type: 'object',
  properties: {
    id: { type: 'string', description: 'Id corto en kebab-case, estable ("packshot", "ambiente-living").' },
    titulo: { type: 'string', description: 'Lo que ve la persona en la tarjeta ("Packshot", "Ambiente"). Hasta 60.' },
    icono: { type: 'string', enum: [...ICONOS_DE_TOMA], description: 'El ícono de la tarjeta, del set fijo.' },
    que_muestra: {
      type: 'string',
      description:
        'La instrucción de la toma para el ESCRITOR, que verá las fotos y los datos de cada producto: qué se ve, encuadre, vista, qué resalta. Sin fondo ni luz si fondo_blanco. Hasta 600.',
    },
    aplica_a: { type: 'array', items: { type: 'string' }, description: 'Ids de producto (los id= de PANTALLA ACTUAL) a los que aplica. Sin este campo, a todos.' },
    cantidad: { type: 'number', description: `Imágenes por producto en esta toma (1 a ${MAX_CANTIDAD_TOMA}). Default 1.` },
    fondo_blanco: { type: 'boolean', description: 'Packshot / catálogo en fondo blanco puro: lo pone el sistema y corrige el punto blanco.' },
    luz_fondo_blanco: { type: 'string', enum: ['difusa', 'dura'] },
    tarea: { type: 'string', enum: [...TAREAS_DE_IMAGEN], description: 'Qué tipo de generación es (crear, componer…); elige el motor en el backend.' },
    referencias: { type: 'array', items: { type: 'string' }, description: 'Ids de las referencias del plan (las imágenes que subió la persona) que viajan con esta toma.' },
    infografia: {
      type: 'boolean',
      description:
        'La FICHA DE MEDIDAS: el escritor no la redacta, llena la plantilla de ficha técnica con las medidas reales del producto y, con pills_de_la_ficha, hasta 4 etiquetas: las que elegiste con etiquetas_de_producto o, si no, datos de su ficha (sin marca, modelo ni códigos). Queda como un prompt más (editable) y viaja con las fotos del producto y las `referencias` de la toma, que la plantilla usa como guía de COMPOSICIÓN de la lámina. Exige medidas. Ícono medidas.',
    },
    pills_de_la_ficha: { type: 'boolean', description: 'Sólo con infografia. Default true: los datos de la ficha (material, capacidad, potencia…) van como etiquetas. false = sólo cotas.' },
    color_infografia: { type: 'string', enum: ['blanco', 'grafito', 'beige'], description: 'Sólo con infografia: el fondo de la lámina. Default blanco.' },
    estilo_grafico: { type: 'string', description: 'Sólo con infografia: colores y tipografía para cotas y etiquetas, en una frase (los de la marca si los tiene; hasta 600).' },
  },
  required: ['id', 'titulo', 'icono', 'que_muestra'],
};

/** Cuántas tomas muestra `ver_tomas` por llamada (con foto, cada una es una imagen en el historial). */
export const MAX_TOMAS_POR_VISTA = 12;

const FILTRO = {
  type: 'object',
  description: 'Qué fotos: por id de ítem (producto__toma), de producto o de toma. Sin esto, todas las generadas.',
  properties: {
    items: { type: 'array', items: { type: 'string' } },
    productos: { type: 'array', items: { type: 'string' } },
    tomas: { type: 'array', items: { type: 'string' } },
  },
};

export const HERRAMIENTAS_DEL_CATALOGO: HerramientaDePantalla[] = [
  {
    name: 'proponer_plan',
    etiqueta: 'Armando el plan del catálogo…',
    description:
      `Arma (o reemplaza) el PLAN del catálogo en la pantalla: el título, el ESTILO COMÚN (luz, paleta, ambiente, cámara: UNA decisión para todo el catálogo), de 1 a ${MAX_TOMAS} TOMAS y, si corresponde, formato y calidad. Mirá los productos ANTES (ver_productos) y leé PANTALLA ACTUAL (ver_pantalla) para los ids. ` +
      'Cada toma es una instrucción para un ESCRITOR del backend que después redacta un prompt por producto con sus fotos y sus datos: en `que_muestra` decí qué se ve y cómo, no escribas el prompt. ' +
      'Reemplazar el plan conserva los prompts que la persona fijó y los de las tomas con foto que siguen (mismo id); una toma que no repetís se va con su foto. Con un plan ya armado, para sumar una toma usá modificar_plan (agregar_toma), no rehagas el plan. No escribe prompts: para eso, después, escribir_prompts. La pantalla muestra las tomas al instante.',
    input_schema: {
      type: 'object',
      properties: {
        titulo: { type: 'string', description: 'Nombre corto del catálogo ("Catálogo invierno 2026").' },
        estilo: { type: 'string', description: 'El estilo común, en prosa (hasta 1.500). Sin nombrar motores ni formato.' },
        formato: { type: 'string', description: 'Slug del formato (post, square, story, portrait, banner, editorial, billboard). Opcional.' },
        resolucion: { type: 'string', enum: ['1K', '2K', '4K'], description: 'Calidad. Opcional; default 2K. 1K y 2K cuestan 1 crédito por imagen, 4K cuesta 2.' },
        tomas: { type: 'array', items: TOMA },
      },
      required: ['titulo', 'estilo', 'tomas'],
    },
  },
  {
    name: 'modificar_plan',
    etiqueta: 'Cambiando el plan…',
    description:
      'Cambia el plan que YA está en la pantalla con operaciones parciales: estilo, titulo, formato, resolucion, agregar_toma, quitar_toma, editar_toma, quitar_producto, quitar_de_producto (saca UNA toma de UN producto: su prompt y su foto salen del catálogo; la foto queda en Creaciones), notas_de_producto, etiquetas_de_producto (las hasta 4 etiquetas de SU ficha de medidas: elegí lo que le importa al comprador —torque, capacidad, material, autonomía—, nunca marca, modelo, códigos ni lo que el nombre ya dice; [] vuelve a las de la ficha), referencia (dice QUÉ ES una imagen del plan o una foto del chat por su adjunto_id —"Plantilla: lámina de la marca", "Fondo: terraza"— y, si era del chat, la suma al plan; después editar_toma la pone en las `referencias` de las tomas que la usan). ' +
      'Lo afectado y no fijado vuelve a "sin prompt": después llamá escribir_prompts. Usá los ids de PANTALLA ACTUAL.',
    input_schema: {
      type: 'object',
      properties: {
        resumen: { type: 'string', description: 'Una frase de qué cambiaste.' },
        operaciones: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              op: { type: 'string', enum: ['estilo', 'titulo', 'formato', 'resolucion', 'agregar_toma', 'quitar_toma', 'editar_toma', 'quitar_producto', 'quitar_de_producto', 'notas_de_producto', 'etiquetas_de_producto', 'referencia'] },
              valor: { type: 'string', description: 'estilo / titulo / formato / resolucion: el valor nuevo.' },
              toma: { ...TOMA, description: 'agregar_toma: la toma entera. editar_toma: los campos a cambiar (con su id).' },
              toma_id: { type: 'string', description: 'quitar_toma / editar_toma / quitar_de_producto: el id de la toma.' },
              producto_id: { type: 'string', description: 'quitar_producto / quitar_de_producto / notas_de_producto / etiquetas_de_producto: el id del producto.' },
              etiquetas: { type: 'array', items: { type: 'string' }, description: 'etiquetas_de_producto: hasta 4, "Clave: valor" corto ("Torque máximo: 10 N·m").' },
              notas: { type: 'string', description: 'notas_de_producto: lo que el escritor tiene que saber de ESE producto (hasta 600).' },
              referencia_id: { type: 'string', description: 'referencia: el id de la referencia del plan, o el adjunto_id de una foto del chat ("m3-1").' },
              etiqueta: { type: 'string', description: 'referencia: qué es, con el rol al principio ("Plantilla: lámina PEKTRA", "Fondo: …", "Logo"). Hasta 120.' },
            },
            required: ['op'],
          },
        },
      },
      required: ['operaciones'],
    },
  },
  {
    name: 'sumar_productos',
    etiqueta: 'Sumando los productos al catálogo…',
    description:
      'Suma al catálogo de la pantalla productos que YA están en el catálogo de la marca (un producto adjunto, uno que nombró, uno recién creado con agregar_productos). Van al final, con sus medidas y su ficha, y reciben las tomas de `tomas` (sin `tomas`, todas las del plan). ' +
      'Sus prompts quedan sin escribir: después llamá escribir_prompts con `solo.productos`. Lo que ya estaba no se duplica. Si mandó fotos de un producto que NO está en el catálogo, primero agregar_productos.',
    input_schema: {
      type: 'object',
      properties: {
        product_ids: { type: 'array', items: { type: 'string' }, description: 'Los ids de los productos (los del adjunto o del catálogo).' },
        tomas: { type: 'array', items: { type: 'string' }, description: 'Ids de las tomas que reciben. Opcional: sin esto, todas.' },
      },
      required: ['product_ids'],
    },
  },
  {
    name: 'cargar_datos_de_productos',
    etiqueta: 'Guardando los datos de los productos…',
    description:
      'Guarda datos de productos en su FICHA (quedan para siempre, no sólo para este catálogo): medidas (alto, ancho, profundidad en cm) y campos libres (material, capacidad, tapizado…). ' +
      'Con una planilla adjunta: leela con ver_planilla, casá cada fila con un producto por SKU o nombre, y mandá acá los que casaron. ' +
      'Lo que la persona ya confirmó NO se pisa salvo `sobrescribir: true`: lo rechazado vuelve en el resultado, preguntale antes de forzar. Devuelve qué se guardó y qué no.',
    input_schema: {
      type: 'object',
      properties: {
        productos: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              producto_id: { type: 'string', description: 'El id del producto (los id= de PANTALLA ACTUAL).' },
              medidas: {
                type: 'object',
                properties: {
                  unidad: { type: 'string', enum: ['cm', 'mm', 'm'] },
                  alto: { type: 'number' },
                  ancho: { type: 'number' },
                  profundidad: { type: 'number' },
                  espesor: { type: 'number' },
                  peso_kg: { type: 'number' },
                },
              },
              campos: {
                type: 'array',
                items: { type: 'object', properties: { clave: { type: 'string' }, valor: { type: 'string' } }, required: ['clave', 'valor'] },
              },
            },
            required: ['producto_id'],
          },
        },
        sobrescribir: { type: 'boolean', description: 'true SÓLO si la persona dijo que pise lo que ya había cargado.' },
      },
      required: ['productos'],
    },
  },
  {
    name: 'escribir_prompts',
    etiqueta: 'Escribiendo los prompts…',
    description:
      'Manda al escritor del backend los ítems sin prompt (o los de `solo`): un pedido por producto, en paralelo, con sus fotos y sus datos. Tarda unos segundos por producto; no cuesta créditos. ' +
      'Con `indicacion` reescribe en masa: el escritor aplica ese cambio puntual a cada toma pedida ("que el piso sea de madera clara"). Una toma que YA TIENE FOTO no se reescribe salvo `aunque_tengan_foto: true`: queda por regenerar (la foto vieja sigue hasta que salga la nueva) y Generar la cobra; usalo sólo si la persona quiere cambiar esas fotos. ' +
      'Los prompts aparecen en la pantalla a medida que llegan: NO los repitas en el chat. Devuelve cuántos salieron y cuáles fallaron (con el motivo). Un ítem fijado por la persona no se reescribe.',
    input_schema: {
      type: 'object',
      properties: {
        solo: {
          type: 'object',
          description: 'Para reescribir sólo una parte (aunque ya esté escrita). Sin esto, los que faltan.',
          properties: {
            items: { type: 'array', items: { type: 'string' }, description: 'Ids de ítem (producto__toma).' },
            productos: { type: 'array', items: { type: 'string' }, description: 'Ids de producto.' },
            tomas: { type: 'array', items: { type: 'string' }, description: 'Ids de toma.' },
          },
        },
        indicacion: {
          type: 'string',
          description: 'Un cambio puntual para ESTA reescritura, para todos los ítems de `solo` (hasta 600). No queda guardado: si tiene que valer siempre, va en la toma, el estilo o las notas del producto.',
        },
        aunque_tengan_foto: {
          type: 'boolean',
          description: 'true = reescribir también las tomas que ya tienen foto (quedan por regenerar y cuestan). Sólo si la persona quiere cambiar esas fotos. Default false: se saltean y te digo cuáles.',
        },
      },
    },
  },
  {
    name: 'ver_tomas',
    etiqueta: 'Mirando las tomas del catálogo…',
    description:
      `Te muestra, de las tomas de \`solo\` (sin \`solo\`, las primeras), el PROMPT entero, el estado, lo pendiente y la FOTO generada de cada una (llega como imagen debajo de su renglón). Hasta ${MAX_TOMAS_POR_VISTA} por llamada; para seguir, \`desde\`. ` +
      'Usala para mirar cómo salió una foto antes de editarla o rehacerla, y para leer un prompt antes de cambiarlo con editar_prompt. No cuesta créditos. Lo que mirás se arrastra en el historial: pedí sólo lo que hace falta.',
    input_schema: {
      type: 'object',
      properties: {
        solo: { ...FILTRO, description: 'Qué tomas: por id de ítem (producto__toma), de producto o de toma. Sin esto, desde la primera.' },
        desde: { type: 'number', description: 'Desde qué toma de la lista seguir, contando desde 1. Default 1.' },
        sin_fotos: { type: 'boolean', description: 'true para traer sólo los prompts, sin las imágenes.' },
      },
    },
  },
  {
    name: 'editar_prompt',
    etiqueta: 'Cambiando un prompt…',
    description:
      'Reemplaza el prompt de UN ítem (producto__toma, el id de PANTALLA ACTUAL) y lo deja FIJADO: el escritor no lo pisa. Leé antes el actual con ver_tomas y cambiá sólo lo pedido. Si la toma ya tiene foto, la foto sigue en la tarjeta y queda por regenerar con el prompt nuevo: sale cuando la persona toca Generar (y saca la edición o el rehacer que hubiera pendiente). Para cambiar muchos: escribir_prompts con `solo` e `indicacion`, o la toma o el estilo con modificar_plan.',
    input_schema: {
      type: 'object',
      properties: {
        item_id: { type: 'string' },
        prompt: { type: 'string', description: 'El prompt entero, con las reglas de imagen de siempre.' },
      },
      required: ['item_id', 'prompt'],
    },
  },
  {
    name: 'editar_imagenes',
    etiqueta: 'Preparando la edición de las fotos…',
    description:
      'Edita en masa las fotos YA GENERADAS (las de `solo`; sin `solo`, todas): cada una se manda a editar con `instruccion` sobre la foto de su tarjeta, que se mantiene igual salvo lo pedido ("fondo gris claro", "sacá la sombra", "la tela en verde"). ' +
      'No lanza nada: deja la edición pendiente en cada tarjeta. Después llamá generar_catalogo, que muestra en Generar cuántas fotos y cuántos créditos (una imagen por foto) y espera el click. La foto editada reemplaza a la de la tarjeta; la vieja queda en Creaciones. ' +
      'Para cambiar lo que muestra la toma de cero (otro ambiente, otro ángulo) no es esto: reescribí con escribir_prompts. Con `cancelar: true` saca las ediciones y los rehacer pendientes de `solo`.',
    input_schema: {
      type: 'object',
      properties: {
        solo: FILTRO,
        instruccion: { type: 'string', description: 'Qué cambiar en cada foto, en una frase (hasta 600). Sin nombrar motores.' },
        cancelar: { type: 'boolean', description: 'true para sacar lo pendiente en vez de marcar.' },
      },
    },
  },
  {
    name: 'rehacer_imagenes',
    etiqueta: 'Preparando las fotos para rehacer…',
    description:
      'Deja para rehacer de cero, con su prompt de siempre, las fotos YA GENERADAS de `solo` (sin `solo`, todas): sirve cuando una salió mal y el prompt está bien. Cuesta lo mismo que generarlas. ' +
      'No lanza nada: después llamá generar_catalogo, que espera el click. Si el prompt tiene que cambiar, reescribilo con escribir_prompts (las tomas con foto quedan por rehacer solas). Con `cancelar: true` saca lo pendiente de `solo`.',
    input_schema: {
      type: 'object',
      properties: {
        solo: FILTRO,
        cancelar: { type: 'boolean', description: 'true para sacar lo pendiente en vez de marcar.' },
      },
    },
  },
  {
    name: 'generar_catalogo',
    etiqueta: 'Esperando que la persona toque Generar…',
    description:
      'SÓLO si la persona pidió generar (o editar o rehacer fotos). Valida que no falte nada, resalta el botón Generar con lo que se lanza y su costo (lo nuevo, las ediciones y los rehacer pendientes; lo ya generado no se relanza) y ESPERA su click (gasta créditos: el click es la confirmación). ' +
      'No la llames para saber el costo: está en PANTALLA ACTUAL. Devuelve cuántas imágenes se lanzaron o por qué no se pudo.',
    input_schema: { type: 'object', properties: {} },
  },
];

export const NOMBRES_HERRAMIENTAS_DEL_CATALOGO: string[] = HERRAMIENTAS_DEL_CATALOGO.map((h) => h.name);
